import { useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RiAddLine, RiCarLine, RiCheckLine, RiCloseLine } from "react-icons/ri";
import Preloader from "../../Components/Preloader";
import { toGetProfil } from "../../apis/actions/auth.actions";
import { toGetMyVehicle, toGetModel } from "../../apis/actions/vehicle.actions";
import { buildSaudiPlateRows } from "../../utils/plateConversions";
import { resolveVehicleModelDisplayName } from "../../utils/vehicleDisplay";
import { setVehicle, setBrowseServicesWithoutVehicle } from "../../apis/slices/globalSlice";
import { AppDispatch, RootState } from "../../apis/store";
import { useTranslation } from "react-i18next";

interface Props {
  visible: boolean;
  setVisible: (v: boolean) => void;
}

const Canvas = ({ visible, setVisible }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { myVehicles, models } = useSelector((state: RootState) => state.vehicle);
  const { vehicle, browseServicesWithoutVehicle } = useSelector((state: RootState) => state.global);
  const navigate = useNavigate();
  const [cookies] = useCookies(["apiKey"]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const cars = myVehicles?.list ?? [];

  useEffect(() => {
    if (cookies.apiKey) {
      dispatch(toGetProfil(i18n.language));
      dispatch(toGetMyVehicle(i18n.language));
      dispatch(toGetModel());
    }
  }, [dispatch, cookies.apiKey, i18n.language]);

  useEffect(() => {
    if (!visible) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    previousFocus.current = document.activeElement as HTMLElement | null;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVisible(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus.current?.focus();
    };
  }, [visible, setVisible]);

  const handleCarSelect = (car: any) => {
    if (car == null) {
      dispatch(setBrowseServicesWithoutVehicle(true));
    } else {
      dispatch(setBrowseServicesWithoutVehicle(false));
      dispatch(setVehicle(car));
    }
    setVisible(false);
    navigate("/services");
  };

  return (
    <>
      <div className={`ac-canvas-backdrop${visible ? " ac-canvas-backdrop--open" : ""}`} aria-hidden="true" onClick={() => setVisible(false)} />
      <div
        className={`sidebar-canvas-wrapper ac-canvas--cars${visible ? " sidebar-canvas-visible" : ""}`}
        role="dialog"
        aria-modal={visible}
        aria-hidden={!visible}
        aria-labelledby="ac-garage-title"
      >
        <Preloader loading={myVehicles.status === "idle"} />
        <div className="sidebar-canvas-container ac-garage">
          <header className="ac-garage__header">
            <div className="ac-garage__topline">
              <span className="ac-garage__eyebrow">{t("profile.car.garage")}</span>
              <button ref={closeRef} type="button" className="ac-garage__close" onClick={() => setVisible(false)} aria-label={t("services.close")}><RiCloseLine aria-hidden="true" /></button>
            </div>
            <div className="ac-garage__hero">
              <span className="ac-garage__hero-icon" aria-hidden="true"><RiCarLine /></span>
              <div><h2 id="ac-garage-title">{t("profile.car.myCars")}</h2><p>{t("profile.car.chooseCarHint")}</p></div>
            </div>
            <span className="ac-garage__count">{t("profile.car.vehicleCount", { count: cars.length })}</span>
          </header>

          <div className="ac-garage__scroll">
            <div className="ac-garage__section-head"><span>{t("profile.car.chooseCar")}</span><span className="ac-garage__section-rule" /></div>
            <button type="button" className={`ac-garage__browse${(browseServicesWithoutVehicle || !vehicle?.id) ? " is-selected" : ""}`} onClick={() => handleCarSelect(null)} aria-pressed={browseServicesWithoutVehicle || !vehicle?.id}>
              <span className="ac-garage__browse-icon" aria-hidden="true"><RiCarLine /></span>
              <span className="ac-garage__browse-copy"><strong>{t("profile.car.noCarSelected")}</strong><small>{t("profile.car.noCarSelectedHint")}</small></span>
              <span className="ac-garage__check" aria-hidden="true">{(browseServicesWithoutVehicle || !vehicle?.id) && <RiCheckLine />}</span>
            </button>

            {cars.length > 0 && <div className="ac-garage__section-head ac-garage__section-head--cars"><span>{t("profile.car.yourVehicles")}</span><span className="ac-garage__section-rule" /></div>}
            <div className="ac-garage__list">
              {cars.map((car: any) => {
                const modelName = resolveVehicleModelDisplayName(car, models?.list);
                const plateNumbers = String(car?.plate_numbers ?? "").trim();
                const plateLetters = String(car?.plate_letters ?? "").trim();
                const plateRows = plateNumbers || plateLetters ? buildSaudiPlateRows(car?.plate_numbers, { plateLetters: car?.plate_letters }) : null;
                const plateAr = plateRows ? `${plateRows.arNums} ${plateRows.arLetters}`.trim() : "";
                const plateEn = plateRows ? `${plateRows.enNums} ${plateRows.enLetters}`.trim() : "";
                const title = String(car?.title ?? "").trim() || modelName || t("profile.car.myCars");
                const isSelected = !browseServicesWithoutVehicle && vehicle?.id === car.id;
                return (
                  <button key={car.id ?? car.vin_sn} type="button" className={`ac-garage-card${isSelected ? " is-selected" : ""}`} onClick={() => handleCarSelect(car)} aria-pressed={isSelected} aria-label={`${title}${modelName && modelName !== title ? `, ${modelName}` : ""}${plateEn ? `, ${plateEn}` : ""}`}>
                    <span className="ac-garage-card__main">
                      <span className="ac-garage-card__image" aria-hidden="true">{car.image_128 ? <img src={car.image_128} alt="" loading="lazy" decoding="async" /> : <RiCarLine />}</span>
                      <span className="ac-garage-card__identity"><strong title={title}>{title}</strong>{modelName && modelName !== title && <small title={modelName}>{modelName}</small>}</span>
                      <span className="ac-garage__check" aria-hidden="true">{isSelected && <RiCheckLine />}</span>
                    </span>
                    {(plateRows || car.vin_sn) && <span className="ac-garage-card__meta">
                      {plateRows && <span className="ac-garage-card__plate" title={`${plateEn} · ${plateAr}`}><span lang="ar" dir="rtl">{plateAr}</span><span lang="en" dir="ltr">{plateEn}</span></span>}
                      {car.vin_sn && <span className="ac-garage-card__vin"><span>{t("profile.car.vin")}</span><b dir="ltr">{car.vin_sn}</b></span>}
                    </span>}
                  </button>
                );
              })}
            </div>
            {cars.length === 0 && myVehicles.status !== "idle" && <p className="ac-garage__empty">{t("profile.car.emptyGarage")}</p>}
          </div>

          <footer className="ac-garage__footer"><Link to="/add-car" className="ac-garage__add" onClick={() => setVisible(false)}><RiAddLine aria-hidden="true" /><span>{t("profile.car.addCar")}</span></Link></footer>
        </div>
      </div>
    </>
  );
};

export default Canvas;
